import React, { createContext, useContext, ReactNode } from 'react';
import container from '../../di/container';
import { TYPES } from '../../di/types';

// Import use case interfaces
import { LoginUseCase } from '../../domain/usecases/AuthenticationUseCases';
import { SearchProductsUseCase, GetAllProductsUseCase, GetProductsByCategoryUseCase, GetProductsAvailableInBranchUseCase, GetProductCategoriesUseCase } from '../../domain/usecases/ProductUseCases';
import { AddItemToCartUseCase, CreateCartUseCase, CancelCartUseCase, UpdateCartItemQuantityUseCase } from '../../domain/usecases/SaleUseCases';

// Define the use cases interface
interface UseCases {
  // Authentication
  loginUseCase: LoginUseCase;
  
  // Products
  searchProductsUseCase: SearchProductsUseCase;
  getAllProductsUseCase: GetAllProductsUseCase;
  getProductsByCategoryUseCase: GetProductsByCategoryUseCase;
  getProductsAvailableInBranchUseCase: GetProductsAvailableInBranchUseCase;
  getProductCategoriesUseCase: GetProductCategoriesUseCase;
  
  // Sales
  addItemToCartUseCase: AddItemToCartUseCase;
  createCartUseCase: CreateCartUseCase;
  cancelCartUseCase: CancelCartUseCase;
  updateCartItemQuantityUseCase: UpdateCartItemQuantityUseCase;
}

// Create context
const DIContext = createContext<UseCases | null>(null);

// Provider component
interface DIProviderProps {
  children: ReactNode;
}

export const DIProvider: React.FC<DIProviderProps> = ({ children }) => {
  // Get use cases from the DI container
  const useCases: UseCases = {
    // Authentication
    loginUseCase: container.get<LoginUseCase>(TYPES.LoginUseCase),
    
    // Products
    searchProductsUseCase: container.get<SearchProductsUseCase>(TYPES.SearchProductsUseCase),
    getAllProductsUseCase: container.get<GetAllProductsUseCase>(TYPES.GetAllProductsUseCase),
    getProductsByCategoryUseCase: container.get<GetProductsByCategoryUseCase>(TYPES.GetProductsByCategoryUseCase),
    getProductsAvailableInBranchUseCase: container.get<GetProductsAvailableInBranchUseCase>(TYPES.GetProductsAvailableInBranchUseCase),
    getProductCategoriesUseCase: container.get<GetProductCategoriesUseCase>(TYPES.GetProductCategoriesUseCase),
    
    // Sales
    addItemToCartUseCase: container.get<AddItemToCartUseCase>(TYPES.AddItemToCartUseCase),
    createCartUseCase: container.get<CreateCartUseCase>(TYPES.CreateCartUseCase),
    cancelCartUseCase: container.get<CancelCartUseCase>(TYPES.CancelCartUseCase),
    updateCartItemQuantityUseCase: container.get<UpdateCartItemQuantityUseCase>(TYPES.UpdateCartItemQuantityUseCase),
  };

  return (
    <DIContext.Provider value={useCases}>
      {children}
    </DIContext.Provider>
  );
};

// Hook to use the DI context
export const useUseCases = (): UseCases => {
  const context = useContext(DIContext);
  if (!context) {
    throw new Error('useUseCases must be used within a DIProvider');
  }
  return context;
};

export default DIContext; 